import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormValidation, formValidationMethods } from '@pattern-library/base';
import { Button, FlexButtonGroup } from '@pattern-library/buttons';
import PhoneInput, {
    validatePhoneNumberForRegion,
    getRegionCodeForNumber
} from '@pattern-library/phone-input';

const { matchesPattern } = formValidationMethods;

const propTypes = {
    formAction: PropTypes.string.isRequired,
    antiForgeryToken: PropTypes.string.isRequired,
    callButtonProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
        .isRequired,
    textButtonProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
        .isRequired,
    phoneInputProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
        .isRequired
};

function ConfirmPhoneNumberForm(props) {
    const { formAction, antiForgeryToken, callButtonProps, textButtonProps, phoneInputProps } =
        props;

    const [countryCode, setCountryCode] = useState('us');

    /**
     * Run the given `validationFn` if the phone number is a US number.
     * Otherwise, return true.
     */
    function ifUS(validationFn) {
        return value => {
            return countryCode === 'us' ? validationFn(value) : true;
        };
    }

    const schema = {
        [phoneInputProps.name]: [
            {
                validationMethod: 'required',
                errorMessage: 'Enter a phone number'
            },
            {
                // eslint-disable-next-line no-useless-escape
                validationMethod: ifUS(value => matchesPattern(value, /^\+1 \d{3}\-\d{3}-\d{4}$/)),
                errorMessage: 'Phone number must be 10 digits'
            },
            {
                validationMethod: async value => {
                    try {
                        const regionCode = await getRegionCodeForNumber(value, countryCode);
                        if (!regionCode) {
                            return false;
                        }
                        return await validatePhoneNumberForRegion(value, regionCode);
                    } catch (errorMessage) {
                        console.error(`errorMessage: ${errorMessage}`);
                        return false;
                    }
                },
                errorMessage: 'Phone number is not valid'
            }
        ]
    };
    const { hasError, errorMessage, onSubmit } = useFormValidation({ schema });

    return (
        <form action={formAction} method="post" onSubmit={onSubmit} autoComplete="off" noValidate>
            <div dangerouslySetInnerHTML={{ __html: antiForgeryToken }} />
            <PhoneInput
                {...phoneInputProps}
                onChange={data => {
                    setCountryCode(data.countryCode);
                }}
                defaultCountry="us"
                isInternational
                isRequired
                hasError={hasError(phoneInputProps.name)}
                errorMessage={[].concat(errorMessage(phoneInputProps.name))[0]}
            />

            <FlexButtonGroup alignment="center" className="asc-mt-4 asc-mb-2">
                <Button
                    type="submit"
                    className="asc-mtb-0 asc-btn--secondary"
                    {...callButtonProps}
                />
                <Button
                    type="submit"
                    className="asc-mtb-0"
                    {...textButtonProps}
                    customAttrs={{
                        ...textButtonProps.customAttrs,
                        'aria-describedby': 'text-fees-notice'
                    }}
                />
            </FlexButtonGroup>

            <p className="asc-p--footnote asc-text--center asc-mtb-0" id="text-fees-notice">
                Standard text messaging fees may apply.
            </p>
        </form>
    );
}

ConfirmPhoneNumberForm.propTypes = propTypes;
export default ConfirmPhoneNumberForm;
